import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  InputBase,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Search,
  PersonOutline,
  Assignment,
  Description,
  KeyboardReturn,
  NorthWest,
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetStudentsQuery } from "../../services/students.service";
import { useGetTasksQuery } from "../../services/tasks.service";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { useAppTheme } from "../../context/ThemeContext";

type ResultItem = {
  id: string;
  type: "student" | "task" | "submission";
  primary: string;
  secondary: string;
  href: string;
};

const MAX_PER_GROUP = 4;

function highlight(text: string, query: string) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="mark" sx={{ bgcolor: "warning.light", color: "inherit", borderRadius: "2px", px: 0.2 }}>
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
}

const TYPE_META = {
  student:    { label: "Students",    icon: <PersonOutline sx={{ fontSize: 14 }} />, color: "#0EA5E9" },
  task:       { label: "Tasks",       icon: <Assignment   sx={{ fontSize: 14 }} />, color: "#F59E0B" },
  submission: { label: "Submissions", icon: <Description  sx={{ fontSize: 14 }} />, color: "#8B5CF6" },
};

function GlobalSearch() {
  const { accentColor, isNewTheme } = useAppTheme();
  const navigate = useNavigate();

  const { data: students } = useGetStudentsQuery("Students");
  const { data: tasks }    = useGetTasksQuery("Tasks");
  const { data: submissions } = useGetSubmissionsQuery("Submissions");

  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState("");
  const [cursor, setCursor] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Build results
  const results: ResultItem[] = [];
  const q = query.trim().toLowerCase();

  if (q.length >= 1) {
    (students ?? []).forEach((s) => {
      if (
        s.fullName?.toLowerCase().includes(q) ||
        s.refNo?.toLowerCase().includes(q) ||
        (s.email as string)?.toLowerCase().includes(q) ||
        (s.phone as string)?.toLowerCase().includes(q)
      ) {
        results.push({
          id: `s-${s._id}`,
          type: "student",
          primary: s.fullName,
          secondary: `${s.refNo} · ${s.status}`,
          href: `/students/${s._id}`,
        });
      }
    });

    (tasks ?? []).forEach((t) => {
      if (
        (t.name as string)?.toLowerCase().includes(q) ||
        (t.refNo as string)?.toLowerCase().includes(q)
      ) {
        results.push({
          id: `t-${t._id}`,
          type: "task",
          primary: t.name as string,
          secondary: `${t.status} · ${t.urgency}`,
          href: "/tasks",
        });
      }
    });

    (submissions ?? []).forEach((s) => {
      if (
        s.fullName?.toLowerCase().includes(q) ||
        s.refNo?.toLowerCase().includes(q) ||
        (s.email as string)?.toLowerCase().includes(q)
      ) {
        results.push({
          id: `sub-${s._id}`,
          type: "submission",
          primary: s.fullName,
          secondary: `${s.refNo} · ${s.status}`,
          href: "/submissions",
        });
      }
    });
  }

  // Group and cap per type
  const grouped = (["student", "task", "submission"] as const)
    .map((type) => ({
      type,
      items: results.filter((r) => r.type === type).slice(0, MAX_PER_GROUP),
    }))
    .filter((g) => g.items.length > 0);

  const flat = grouped.flatMap((g) => g.items);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, flat.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === "Enter") {
        if (cursor >= 0 && flat[cursor]) {
          navigate(flat[cursor].href);
          handleClose();
        }
      } else if (e.key === "Escape") {
        handleClose();
      }
    },
    [cursor, flat, navigate]
  );

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    setCursor(-1);
  };

  const handleSelect = (href: string) => {
    navigate(href);
    handleClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative" }}>
        {/* Input trigger */}
        <Box
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            height: 36,
            borderRadius: "8px",
            border: `1px solid ${open ? accentColor : isNewTheme ? "#E2E8F0" : "rgba(255,255,255,0.2)"}`,
            bgcolor: isNewTheme
              ? open ? "#fff" : "#F8FAFC"
              : "rgba(255,255,255,0.12)",
            cursor: "text",
            transition: "all 0.15s ease",
            width: open ? 320 : 200,
            "&:hover": {
              borderColor: accentColor,
              bgcolor: isNewTheme ? "#fff" : "rgba(255,255,255,0.18)",
            },
          }}
        >
          <Search sx={{ fontSize: 16, color: open ? accentColor : isNewTheme ? "#94A3B8" : "rgba(255,255,255,0.6)", flexShrink: 0 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(-1); setOpen(true); }}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            placeholder="Search…"
            sx={{
              flex: 1,
              fontSize: "0.82rem",
              color: isNewTheme ? "text.primary" : "rgba(255,255,255,0.9)",
              "& input::placeholder": { color: isNewTheme ? "#94A3B8" : "rgba(255,255,255,0.5)", opacity: 1 },
            }}
          />
          {!open && (
            <Chip
              label="⌘K"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 600,
                bgcolor: isNewTheme ? "#F1F5F9" : "rgba(255,255,255,0.15)",
                color: isNewTheme ? "#64748B" : "rgba(255,255,255,0.6)",
                border: "none",
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Results dropdown */}
        {open && (
          <Paper
            elevation={0}
            sx={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              zIndex: 1400,
              borderRadius: "10px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 16px 48px rgba(15,23,42,0.14)",
              overflow: "hidden",
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {q.length === 0 ? (
              <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
                <Search sx={{ fontSize: 28, color: "#CBD5E1", mb: 0.5 }} />
                <Typography variant="body2" color="text.disabled">
                  Search students, tasks, submissions
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
                  Search by name, ref no, email, or phone
                </Typography>
              </Box>
            ) : flat.length === 0 ? (
              <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.disabled">
                  No results for <strong>"{query}"</strong>
                </Typography>
              </Box>
            ) : (
              <>
                {grouped.map((group, gi) => {
                  const meta = TYPE_META[group.type];
                  const groupStart = grouped.slice(0, gi).reduce((s, g) => s + g.items.length, 0);

                  return (
                    <Box key={group.type}>
                      {gi > 0 && <Divider sx={{ borderColor: "#F1F5F9" }} />}

                      {/* Group header */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ px: 2, pt: 1.25, pb: 0.5 }}
                      >
                        <Box sx={{ color: meta.color, display: "flex" }}>{meta.icon}</Box>
                        <Typography variant="caption" fontWeight={700} sx={{ color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.65rem" }}>
                          {meta.label}
                        </Typography>
                        <Chip label={group.items.length} size="small" sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, bgcolor: meta.color + "18", color: meta.color, ml: 0.5 }} />
                      </Stack>

                      {/* Items */}
                      {group.items.map((item, ii) => {
                        const idx = groupStart + ii;
                        const active = cursor === idx;

                        return (
                          <Box
                            key={item.id}
                            onClick={() => handleSelect(item.href)}
                            onMouseEnter={() => setCursor(idx)}
                            sx={{
                              px: 2,
                              py: 1,
                              cursor: "pointer",
                              bgcolor: active ? `${accentColor}0e` : "transparent",
                              borderLeft: active ? `3px solid ${accentColor}` : "3px solid transparent",
                              transition: "background 0.1s, border-color 0.1s",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: meta.color + "20",
                                color: meta.color,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {item.primary.slice(0, 2).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {highlight(item.primary, query)}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: "0.65rem" }}>
                                {item.secondary}
                              </Typography>
                            </Box>
                            {active && (
                              <KeyboardReturn sx={{ fontSize: 13, color: "#94A3B8", flexShrink: 0 }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}

                {/* Footer hint */}
                <Divider sx={{ borderColor: "#F1F5F9" }} />
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1, bgcolor: "#FAFBFC" }}>
                  <Stack direction="row" spacing={0.3} alignItems="center">
                    <Chip label="↑↓" size="small" sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, bgcolor: "#F1F5F9", color: "#64748B" }} />
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem" }}>navigate</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.3} alignItems="center">
                    <Chip label="↵" size="small" sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, bgcolor: "#F1F5F9", color: "#64748B" }} />
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem" }}>open</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.3} alignItems="center">
                    <Chip label="esc" size="small" sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, bgcolor: "#F1F5F9", color: "#64748B" }} />
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem" }}>close</Typography>
                  </Stack>
                </Stack>
              </>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

export default GlobalSearch;
